import { Globe, Search, Map, Languages, BarChart3, Swords, Network } from "lucide-react";
import type { MoneyPageContent } from "./types";

/**
 * International SEO service page.
 * Focused on competing across countries/languages/markets broadly.
 * Distinct from /servicios/seo-bilingue-mercado-hispano (US Hispanic market specifically).
 */
export const internationalSeoPage: MoneyPageContent = {
  id: "international-seo",
  icon: Globe,
  serviceType: "International SEO",
  esPath: "/servicios/seo-internacional",
  enPath: "/en/services/international-seo",
  es: {
    metaTitle: "SEO Internacional | Estrategia por país e idioma",
    metaDescription:
      "SEO internacional para competir en varios mercados: investigación por país, arquitectura hreflang, localización de contenido y medición segmentada.",
    keywords: [
      "seo internacional",
      "hreflang",
      "seo multipaís",
      "seo multilingüe",
      "expansión internacional seo",
      "localización de contenido",
    ],
    h1: "SEO Internacional para competir entre mercados, países e idiomas",
    breadcrumbLabel: "SEO Internacional",
    heroSubtitle: "Expandirse no es traducir",
    heroDescription:
      "Competir en varios países no es traducir tus páginas a otro idioma. Es investigar demanda real por mercado, decidir la arquitectura correcta, implementar señales técnicas como hreflang y adaptar contenido a monedas, unidades e intención local. Trabajo con fuerza particular en Estados Unidos y mercados de habla hispana, sin limitarme a ellos.",
    ctaPrimary: {
      label: "Evaluar una estrategia SEO internacional",
      to: "/diagnostico-seo-gratis",
    },
    ctaSecondary: { label: "Ver casos documentados", to: "/casos-de-exito" },
    summary: [
      {
        label: "Investigación por mercado",
        text: "Qué tan real es la demanda en cada país o idioma antes de invertir en él.",
      },
      {
        label: "Arquitectura internacional",
        text: "Decisión informada entre ccTLD, subdominio o subcarpeta según tu situación.",
      },
      {
        label: "Hreflang e implementación técnica",
        text: "Señales claras para que cada buscador muestre la versión correcta a cada usuario.",
      },
      {
        label: "Localización real de contenido",
        text: "No solo idioma: monedas, unidades, convenciones locales e intención de búsqueda distinta.",
      },
    ],
    explanation: {
      title: "Qué es el SEO internacional (y por qué traducir no basta)",
      definition:
        "El SEO internacional es la disciplina de optimizar un sitio para que compita en múltiples países, idiomas o mercados a la vez, sin que unas versiones canibalicen a otras ni generen confusión en los buscadores sobre a quién mostrar qué. Va mucho más allá de traducir texto: implica investigación de mercado, arquitectura técnica y adaptación real de contenido.",
      whyImportant: {
        title: "Por qué una traducción no es una estrategia",
        text: "Un mismo término puede tener volumen de búsqueda, intención e incluso competidores completamente distintos entre países que comparten idioma. España, México y Estados Unidos hispanohablante no se comportan igual, y tratarlos como un bloque único deja oportunidades sin capturar o presupuesto mal asignado.",
      },
      consequences: {
        title: "Qué pasa cuando se hace sin estrategia",
        text: "Contenido duplicado entre versiones de idioma, hreflang mal implementado que hace que Google muestre la versión equivocada, y expansión a mercados sin validar si existe demanda real de búsqueda para justificar la inversión.",
      },
    },
    problems: {
      title: "Áreas donde trabajo con más frecuencia",
      subtitle: "Cada mercado tiene su propia realidad de búsqueda y competencia",
      items: [
        {
          icon: Search,
          title: "Investigación de mercado y demanda",
          description:
            "Validar si existe volumen de búsqueda real y comportamiento de compra en un mercado antes de invertir en expandirse a él.",
          impact: "Evita construir estrategia sobre un mercado que no tiene demanda suficiente.",
        },
        {
          icon: Languages,
          title: "Investigación de palabras clave y localización",
          description:
            "Las palabras clave no se traducen: se investigan por mercado, porque la intención y el vocabulario cambian entre países del mismo idioma.",
          impact: "Contenido alineado con cómo busca la gente en cada mercado, no con una traducción literal.",
        },
        {
          icon: Map,
          title: "Arquitectura: ccTLD, subdominio o subcarpeta",
          description:
            "Cada estructura tiene implicaciones distintas en autoridad de dominio, señales geográficas y mantenimiento técnico.",
          impact: "Una decisión de arquitectura mal tomada al inicio es costosa de revertir después.",
        },
        {
          icon: Network,
          title: "Implementación de hreflang",
          description:
            "Etiquetas hreflang consistentes entre todas las versiones del sitio, evitando conflictos y referencias rotas entre idiomas y regiones.",
          impact: "Cada usuario ve la versión correcta del sitio según su idioma y ubicación.",
        },
        {
          icon: Globe,
          title: "Localización de contenido",
          description:
            "Adaptación de monedas, unidades de medida, convenciones locales (formato de fecha, tono, referencias culturales) e intención de búsqueda propia de cada mercado.",
          impact: "Contenido que se siente local, no traducido.",
        },
        {
          icon: Swords,
          title: "Panorama competitivo por mercado",
          description:
            "Los competidores que dominan un mercado rara vez son los mismos que dominan otro, incluso compartiendo idioma.",
          impact: "Estrategia de contenido y enlaces ajustada a la competencia real de cada país.",
        },
        {
          icon: BarChart3,
          title: "Medición segmentada por mercado",
          description:
            "Seguimiento de rankings, tráfico y conversión separado por país/idioma, no mezclado en una sola vista global.",
          impact: "Visibilidad clara de qué mercado está funcionando y cuál necesita ajuste.",
        },
      ],
    },
    methodology: {
      title: "Cómo abordo el SEO internacional",
      subtitle: "De la validación de mercado a la medición por región",
      steps: [
        {
          number: "01",
          title: "Validación de mercado",
          description:
            "Análisis de demanda de búsqueda y panorama competitivo en cada mercado candidato antes de recomendar expansión.",
        },
        {
          number: "02",
          title: "Definición de arquitectura",
          description:
            "Recomendación de ccTLD, subdominio o subcarpeta según tu situación de dominio, recursos y objetivos, con el plan de implementación de hreflang correspondiente.",
        },
        {
          number: "03",
          title: "Localización de contenido y keyword research por mercado",
          description:
            "Investigación de palabras clave específica por país e idioma, y adaptación de contenido más allá de la traducción.",
        },
        {
          number: "04",
          title: "Medición segmentada",
          description:
            "Configuración de seguimiento separado por mercado para evaluar qué está funcionando en cada región de forma independiente.",
        },
      ],
      ctaText: "Solicitar diagnóstico estratégico",
      ctaLink: "/diagnostico-seo-gratis",
    },
    scope: {
      includes: {
        title: "Qué incluye este servicio",
        items: [
          "Investigación de demanda y viabilidad por mercado",
          "Investigación de palabras clave localizada por país/idioma",
          "Recomendación de arquitectura (ccTLD, subdominio, subcarpeta)",
          "Implementación y auditoría de hreflang",
          "Guía de localización de contenido (monedas, unidades, convenciones)",
          "Análisis competitivo por mercado",
          "Configuración de medición y reporte segmentado por mercado",
        ],
      },
      notIncludes: {
        title: "Qué no incluye",
        items: [
          "Traducción de textos como servicio (se coordina con traductores nativos)",
          "Gestión de campañas de pago por mercado",
          "Garantía de posicionamiento en ningún mercado específico",
          "SEO enfocado exclusivamente en el mercado hispano de EE. UU. (ver servicio específico)",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Para quién tiene sentido",
        items: [
          "Negocios operando o expandiéndose en más de un país",
          "Sitios con versiones en varios idiomas que compiten entre sí sin querer",
          "Empresas evaluando si vale la pena entrar a un nuevo mercado",
          "Negocios con fuerte presencia en Estados Unidos y mercados de habla hispana buscando extenderla",
        ],
      },
      notFor: {
        title: "Para quién no es el mejor ajuste",
        items: [
          "Negocios que operan en un solo país e idioma",
          "Quien busca solo traducir su sitio sin adaptar estrategia",
          "Expansión a un mercado sin presupuesto para sostener el trabajo a mediano plazo",
        ],
      },
    },
    faq: {
      title: "Preguntas frecuentes",
      items: [
        {
          question: "¿Basta con traducir mi sitio a otro idioma para posicionar en otro país?",
          answer:
            "No. La traducción resuelve el idioma, pero no la intención de búsqueda, el panorama competitivo ni las señales técnicas de segmentación geográfica. Sin eso, es común que las versiones ni siquiera se indexen correctamente por mercado.",
        },
        {
          question: "¿México, España y el mercado hispano de EE. UU. se tratan igual por compartir idioma?",
          answer:
            "No necesariamente. Comparten idioma, pero suelen tener volumen de búsqueda, competidores y hasta vocabulario distintos para el mismo producto o servicio. Cada uno se investiga por separado.",
        },
        {
          question: "¿Cómo se decide entre ccTLD, subdominio o subcarpeta?",
          answer:
            "Depende de tu dominio actual, recursos técnicos disponibles, presupuesto y objetivos de marca por país. No hay una respuesta universal; se evalúa según tu situación específica.",
        },
        {
          question: "¿Ustedes trabajan solo con inglés y español?",
          answer:
            "Mi fuerza particular está en Estados Unidos y mercados de habla hispana, pero el trabajo de estrategia, arquitectura y medición aplica a otros idiomas también, coordinando la parte lingüística con hablantes nativos cuando es necesario.",
        },
        {
          question: "¿En cuánto tiempo se ve resultado en un nuevo mercado?",
          answer:
            "Varía según qué tan competido esté el mercado y la autoridad que ya tenga tu dominio ahí. No prometo plazos fijos; sí puedo ayudarte a validar si el mercado justifica la inversión antes de empezar.",
        },
      ],
    },
    related: {
      title: "Servicios relacionados",
      subtitle: "El SEO internacional se apoya en otras disciplinas",
      links: [
        {
          label: "SEO bilingüe para el mercado hispano de EE. UU.",
          description: "Estrategia específica para el consumidor hispano dentro de Estados Unidos.",
          to: "/servicios/seo-bilingue-mercado-hispano",
        },
        {
          label: "SEO Técnico",
          description: "Arquitectura, rastreo e indexación que sostienen la estrategia internacional.",
          to: "/servicios/seo-tecnico",
        },
        {
          label: "Auditoría SEO profesional",
          description: "Diagnóstico documentado antes de decidir una expansión internacional.",
          to: "/servicios/seo-diagnostico",
        },
        {
          label: "SEO para ecommerce",
          description: "Consideraciones específicas de catálogo y checkout multi-país.",
          to: "/servicios/ecommerce-seo",
        },
        {
          label: "Consultoría SEO",
          description: "Acompañamiento estratégico continuo para decisiones de expansión.",
          to: "/consultor-seo",
        },
      ],
    },
    llmSummary:
      "Servicio de SEO internacional: investigación de mercado, arquitectura (ccTLD/subdominio/subcarpeta), hreflang, localización de contenido y medición segmentada por país e idioma, con fuerza particular en EE. UU. y mercados hispanohablantes.",
    keyPoints: [
      "No es traducir el sitio; incluye investigación de mercado y de palabras clave por país",
      "Cubre decisión de arquitectura, hreflang y localización real de contenido",
      "Medición y reporte segmentados por mercado",
      "Fuerza particular en EE. UU. y mercados de habla hispana, sin limitarse a ellos",
    ],
  },
  en: {
    metaTitle: "International SEO Services | Multi-Country Strategy",
    metaDescription:
      "International SEO to compete across markets: market research, hreflang architecture, content localization and market-segmented measurement.",
    keywords: [
      "international seo",
      "hreflang",
      "multi-country seo",
      "multilingual seo",
      "global seo strategy",
      "content localization",
    ],
    h1: "International SEO to compete across markets, countries and languages",
    breadcrumbLabel: "International SEO",
    heroSubtitle: "Expanding isn't translating",
    heroDescription:
      "Competing across countries isn't about translating your pages into another language. It's researching real demand per market, choosing the right architecture, implementing technical signals like hreflang, and adapting content to currencies, units and local intent. I have particular strength in the United States and Spanish-speaking markets, without limiting the service to only those markets.",
    ctaPrimary: {
      label: "Evaluate an international SEO strategy",
      to: "/en/free-seo-diagnosis",
    },
    ctaSecondary: { label: "See documented work", to: "/en/case-studies" },
    summary: [
      {
        label: "Market research",
        text: "How real the demand actually is in a given country or language before you invest in it.",
      },
      {
        label: "International architecture",
        text: "An informed choice between ccTLD, subdomain or subfolder based on your situation.",
      },
      {
        label: "Hreflang & technical setup",
        text: "Clear signals so each search engine shows the right version to the right user.",
      },
      {
        label: "Real content localization",
        text: "Not just language — currencies, units, local conventions and distinct search intent.",
      },
    ],
    explanation: {
      title: "What international SEO is (and why translation isn't enough)",
      definition:
        "International SEO is the discipline of optimizing a site to compete across multiple countries, languages or markets at once, without different versions cannibalizing each other or confusing search engines about who to show what to. It goes well beyond translating text — it involves market research, technical architecture, and real content adaptation.",
      whyImportant: {
        title: "Why translation isn't a strategy",
        text: "The same term can have completely different search volume, intent, and even competitors between countries that share a language. Spain, Mexico and Spanish-speaking audiences in the US don't behave the same way, and treating them as a single block leaves opportunity uncaptured or budget misallocated.",
      },
      consequences: {
        title: "What happens without a strategy",
        text: "Duplicate content between language versions, broken hreflang that causes Google to show the wrong version, and expansion into markets without validating whether real search demand justifies the investment.",
      },
    },
    problems: {
      title: "Areas I work with most often",
      subtitle: "Every market has its own search and competitive reality",
      items: [
        {
          icon: Search,
          title: "Market research & demand validation",
          description:
            "Confirming there's real search volume and buying behavior in a market before investing in expanding into it.",
          impact: "Avoids building a strategy on top of a market with insufficient demand.",
        },
        {
          icon: Languages,
          title: "Keyword research & localization",
          description:
            "Keywords aren't translated — they're researched per market, because intent and vocabulary shift between countries that share a language.",
          impact: "Content aligned with how people actually search in each market, not a literal translation.",
        },
        {
          icon: Map,
          title: "Architecture: ccTLD, subdomain or subfolder",
          description:
            "Each structure carries different implications for domain authority, geo-targeting signals, and technical maintenance.",
          impact: "A poorly made early architecture decision is expensive to reverse later.",
        },
        {
          icon: Network,
          title: "Hreflang implementation",
          description:
            "Consistent hreflang tags across every site version, avoiding conflicts and broken references between languages and regions.",
          impact: "Each user sees the correct version of the site for their language and location.",
        },
        {
          icon: Globe,
          title: "Content localization",
          description:
            "Adapting currencies, units of measurement, local conventions (date formats, tone, cultural references) and market-specific search intent.",
          impact: "Content that feels local, not translated.",
        },
        {
          icon: Swords,
          title: "Competitive landscape per market",
          description:
            "The competitors dominating one market are rarely the same ones dominating another, even when they share a language.",
          impact: "Content and link strategy adjusted to the actual competition in each country.",
        },
        {
          icon: BarChart3,
          title: "Market-segmented measurement",
          description:
            "Tracking rankings, traffic and conversion separately by country/language, not blended into one global view.",
          impact: "Clear visibility into which market is working and which needs adjustment.",
        },
      ],
    },
    methodology: {
      title: "How I approach international SEO",
      subtitle: "From market validation to region-level measurement",
      steps: [
        {
          number: "01",
          title: "Market validation",
          description:
            "Analysis of search demand and competitive landscape in each candidate market before recommending expansion.",
        },
        {
          number: "02",
          title: "Architecture definition",
          description:
            "Recommending ccTLD, subdomain or subfolder based on your domain situation, resources and per-country brand goals, with a corresponding hreflang implementation plan.",
        },
        {
          number: "03",
          title: "Content localization & per-market keyword research",
          description:
            "Country- and language-specific keyword research, and content adaptation that goes beyond translation.",
        },
        {
          number: "04",
          title: "Segmented measurement",
          description:
            "Setting up tracking separated by market so you can evaluate what's working in each region independently.",
        },
      ],
      ctaText: "Request a Strategic SEO Review",
      ctaLink: "/en/free-seo-diagnosis",
    },
    scope: {
      includes: {
        title: "What's included",
        items: [
          "Demand and feasibility research per market",
          "Localized keyword research per country/language",
          "Architecture recommendation (ccTLD, subdomain, subfolder)",
          "Hreflang implementation and audit",
          "Content localization guidance (currencies, units, conventions)",
          "Competitive analysis per market",
          "Market-segmented measurement and reporting setup",
        ],
      },
      notIncludes: {
        title: "What's not included",
        items: [
          "Translation services (coordinated with native-language translators)",
          "Paid media campaign management per market",
          "A ranking guarantee in any specific market",
          "SEO focused exclusively on the US Hispanic market (see dedicated service)",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Who this is a good fit for",
        items: [
          "Businesses operating in or expanding into more than one country",
          "Sites with multiple language versions unintentionally competing with each other",
          "Companies evaluating whether a new market is worth entering",
          "Businesses with a strong presence in the US and Spanish-speaking markets looking to extend it",
        ],
      },
      notFor: {
        title: "Who this isn't the best fit for",
        items: [
          "Businesses operating in a single country and language",
          "Anyone looking to simply translate a site without adapting strategy",
          "Expansion into a market without budget to sustain the work medium-term",
        ],
      },
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        {
          question: "Is translating my site enough to rank in another country?",
          answer:
            "No. Translation solves the language, but not search intent, the competitive landscape, or the technical signals search engines use for geo-targeting. Without those, translated versions often don't even get indexed correctly per market.",
        },
        {
          question: "Are Mexico, Spain and the US Hispanic market treated the same because they share a language?",
          answer:
            "Not necessarily. They share a language but often have different search volume, competitors, and even vocabulary for the same product or service. Each is researched separately.",
        },
        {
          question: "How do you decide between ccTLD, subdomain or subfolder?",
          answer:
            "It depends on your current domain, available technical resources, budget, and per-country brand goals. There's no universal answer — it's evaluated against your specific situation.",
        },
        {
          question: "Do you only work in English and Spanish?",
          answer:
            "My particular strength is the United States and Spanish-speaking markets, but the strategy, architecture and measurement work applies to other languages too, coordinating the linguistic side with native speakers when needed.",
        },
        {
          question: "How long until I see results in a new market?",
          answer:
            "It varies based on how competitive the market is and the authority your domain already has there. I don't promise fixed timelines, but I can help you validate whether the market justifies the investment before you start.",
        },
      ],
    },
    related: {
      title: "Related services",
      subtitle: "International SEO relies on other disciplines",
      links: [
        {
          label: "Bilingual SEO for the US Hispanic market",
          description: "Strategy built specifically for the Hispanic consumer within the United States.",
          to: "/en/services/hispanic-market-seo",
        },
        {
          label: "Technical SEO",
          description: "Architecture, crawling and indexing that support the international strategy.",
          to: "/en/services/technical-seo",
        },
        {
          label: "Professional SEO Audit",
          description: "A documented diagnosis before deciding on international expansion.",
          to: "/en/services/seo-audit",
        },
        {
          label: "Ecommerce SEO",
          description: "Specific considerations for multi-country catalogs and checkout.",
          to: "/en/services/ecommerce-seo",
        },
        {
          label: "SEO Consulting",
          description: "Ongoing strategic guidance for expansion decisions.",
          to: "/en/seo-consultant",
        },
      ],
    },
    llmSummary:
      "International SEO service: market research, architecture (ccTLD/subdomain/subfolder), hreflang, content localization and market-segmented measurement, with particular strength in the US and Spanish-speaking markets.",
    keyPoints: [
      "Not a translation service — includes market and keyword research per country",
      "Covers architecture decisions, hreflang, and real content localization",
      "Measurement and reporting segmented by market",
      "Particular strength in US and Spanish-speaking markets, without being limited to them",
    ],
  },
};
